<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import AuthButton from '@/components/AuthButton.vue'
import InfoBadge from '@/components/InfoBadge.vue'
import OrgLogo from '@/components/OrgLogo.vue'
import ReloadPrompt from '@/components/ReloadPrompt.vue'
import { resolveLocalizedString } from '@/composables/useLocalizedString'
import { PLATFORM_LANGUAGES } from '@/config/languages'
import { type SupportedLocale, saveLocale } from '@/plugins/i18n'
import { useAuthStore } from '@/stores/auth'
import { useMFStore } from '@/stores/mf'
import { useModulesStore } from '@/stores/modules'
import { useOrgStore } from '@/stores/org'
import { useThemeStore } from '@/stores/theme'

const themeStore = useThemeStore()
const orgStore = useOrgStore()
const modulesStore = useModulesStore()
const authStore = useAuthStore()
const mfStore = useMFStore()
const route = useRoute()
const { locale, t } = useI18n()

// Состояние инициализации Shell
const shellReady = ref(false)
const initError = ref<string | null>(null)

// Инициализация Shell при загрузке
onMounted(async () => {
  try {
    // 1. Инициализация темы (синхронно)
    themeStore.initTheme()

    // 2. Восстановление сессии авторизации из localStorage
    authStore.init()

    // 3. Определение организации и загрузка её данных
    await orgStore.initOrg()

    // 4. Загрузка модулей для организации
    await modulesStore.fetchModules(orgStore.orgSlug)

    // 5. Инициализация MF Loader и загрузка манифестов
    console.log('[Shell] Starting MF init...')
    const mfManifests = await mfStore.init()
    console.log('[Shell] MF init complete, manifests:', mfManifests)

    // 6. Загрузка конфигурации модулей (иерархия: default → portal → org)
    await mfStore.loadModulesConfig(orgStore.orgSlug)
    console.log(
      '[Shell] Module config loaded, enabled MFs:',
      mfStore.enabledManifests.map((m) => m.id)
    )

    console.log('[Shell] Initialized:', {
      org: orgStore.orgSlug,
      source: orgStore.orgSource,
      modules: modulesStore.manifests.length,
      mfs: mfStore.manifests.map((m) => m.id),
      enabledMfs: mfStore.enabledManifests.map((m) => m.id),
      authenticated: authStore.isAuthenticated,
    })

    shellReady.value = true

    // 6. Монтируем глобальные виджеты MF с autoMount: true (consultant FAB и др.)
    console.log('[Shell] Mounting auto-mount widgets with locale:', locale.value)
    await mfStore.mountAutoMountWidgets({
      locale: locale.value,
      theme: themeStore.isDark ? 'dark' : 'light',
    })

    // 7. Принудительно обновляем локаль во всех MF после монтирования
    // Это гарантирует, что консультант получит правильную локаль
    mfStore.updateLocale(locale.value)
  } catch (e) {
    console.error('[Shell] Init error:', e)
    initError.value = e instanceof Error ? e.message : 'Failed to initialize'
    // Показываем UI даже при ошибке
    shellReady.value = true
  }
})

// Размонтируем все MF при закрытии
onUnmounted(() => {
  mfStore.unmountAutoMountWidgets()
  mfStore.unmountAll()
})

// Следим за изменением организации — перезагружаем модули
watch(
  () => orgStore.orgSlug,
  async (newSlug, oldSlug) => {
    if (newSlug && newSlug !== oldSlug && shellReady.value) {
      console.log('[Shell] Org changed, reloading modules:', newSlug)
      await modulesStore.fetchModules(newSlug)
    }
  }
)

// Следим за изменением темы — обновляем все MF
watch(
  () => themeStore.isDark,
  (isDark) => {
    mfStore.updateTheme(isDark ? 'dark' : 'light')
  }
)

// Следим за изменением языка — обновляем все MF
// immediate: true гарантирует, что локаль будет отправлена сразу при старте
watch(
  () => locale.value,
  (newLocale) => {
    console.log('[Shell] Locale changed, updating MFs:', newLocale)
    mfStore.updateLocale(newLocale)
  },
  { immediate: true }
)

// Следим за изменением языка — обновляем document.title
watch(
  () => locale.value,
  (newLocale) => {
    const appName = orgStore.branding.appName
    document.title = resolveLocalizedString(appName, newLocale)
  },
  { immediate: true }
)

// Следим за изменением маршрута — обновляем контекст страницы для глобальных виджетов (consultant)
watch(
  () => route.fullPath,
  (fullPath) => {
    if (!shellReady.value) return

    // Определяем, находимся ли внутри MF
    const mfMatch = fullPath.match(/^\/mf\/([^/]+)/)
    const mfId = mfMatch?.[1]
    const mfManifest = mfId ? mfStore.getManifest(mfId) : undefined

    // Получаем локализованное название MF
    let mfTitle: string | undefined
    if (mfManifest?.title) {
      if (typeof mfManifest.title === 'string') {
        mfTitle = mfManifest.title
      } else {
        mfTitle = mfManifest.title[locale.value] || mfManifest.title.ru || mfManifest.title.en
      }
    }

    // Отправляем контекст страницы
    mfStore.updatePageContext({
      route: fullPath,
      params: route.params as Record<string, string>,
      mfId,
      mfTitle,
    })
  },
  { immediate: true }
)

// Получить двуязычную метку для языка
function getBilingualLabel(code: string): string {
  const platformLang = PLATFORM_LANGUAGES.find((l) => l.code === code)
  const nativeName = platformLang?.nativeName ?? code.toUpperCase()
  const localizedName = t(`languages.${code}`)

  // Если текущая локаль совпадает с языком, показываем только нативное название
  if (localizedName === nativeName || locale.value === code) {
    return nativeName
  }
  return `${localizedName} (${nativeName})`
}

// Языки из настроек организации с двуязычными метками
const orgLanguages = computed(() => {
  return orgStore.languages.map((code) => {
    const platformLang = PLATFORM_LANGUAGES.find((l) => l.code === code)
    return {
      code,
      nativeName: platformLang?.nativeName ?? code.toUpperCase(),
    }
  })
})

function setLanguage(code: string) {
  locale.value = code
  saveLocale(code as SupportedLocale)
}
</script>

<template>
  <v-app :theme="themeStore.currentTheme">
    <!-- Loading state -->
    <template v-if="!shellReady">
      <v-main class="d-flex align-center justify-center">
        <div class="text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            class="mb-4"
          />
          <p class="text-body-2 text-muted">{{ t('app.loading') }}</p>
        </div>
      </v-main>
    </template>

    <!-- Main content -->
    <template v-else>
      <!-- App Bar -->
      <v-app-bar class="app-header" elevation="0">
        <v-app-bar-title class="font-weight-bold">
          <router-link to="/" class="text-decoration-none">
            <OrgLogo size="md" :animated="true" />
          </router-link>
        </v-app-bar-title>

        <v-spacer />

        <!-- Переключатель темы -->
        <v-btn variant="plain" size="small" @click="themeStore.toggleTheme">
          <v-icon :icon="themeStore.isDark ? 'mdi-weather-night' : 'mdi-weather-sunny'" />
        </v-btn>

        <!-- Выбор языка -->
        <v-menu content-class="lang-menu-wrapper">
          <template #activator="{ props }">
            <v-btn v-bind="props" variant="text" class="text-none">
              <span class="text-uppercase font-weight-medium">{{ locale }}</span>
              <v-icon end icon="mdi-chevron-down" size="small" />
            </v-btn>
          </template>
          <v-card class="lang-menu" :theme="themeStore.currentTheme">
            <v-list density="compact" nav>
              <v-list-item
                v-for="lang in orgLanguages"
                :key="lang.code"
                :active="lang.code === locale"
                @click="setLanguage(lang.code)"
              >
                <v-list-item-title>{{ getBilingualLabel(lang.code) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <!-- Кнопка авторизации -->
        <AuthButton class="ml-2 mr-4" />
      </v-app-bar>

      <!-- Main content -->
      <v-main>
        <!-- Error banner -->
        <v-alert
          v-if="initError"
          type="warning"
          variant="tonal"
          closable
          class="ma-4"
          @click:close="initError = null"
        >
          {{ initError }}
        </v-alert>

        <router-view />
      </v-main>

      <!-- Info badge -->
      <InfoBadge />

      <!-- PWA reload prompt -->
      <ReloadPrompt />
    </template>
  </v-app>
</template>

<style lang="scss">
// Header с glassmorphism
.app-header {
  @include glass.glass-nav;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;

  .v-theme--glassLight & {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
  }
}

.text-gradient {
  background: linear-gradient(135deg, var(--tlh-primary), var(--tlh-secondary));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

// Обёртка dropdown
.v-overlay__content.lang-menu-wrapper {
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  background: transparent !important;
  border-radius: 8px !important;
}

// Dropdown меню для выбора языка
.lang-menu {
  border-radius: 8px !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  &.v-card {
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    outline: none !important;
    border-radius: 8px !important;
  }

  // Тёмная тема
  .v-theme--glassDark &.v-card {
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  }

  .v-list {
    border: none !important;
    outline: none !important;
    border-radius: 8px !important;
    padding: 4px !important;
  }

  .v-list-item {
    border-radius: 6px !important;
    margin: 2px 0;
    min-height: 36px;

    .v-list-item__overlay {
      background: transparent !important;
      opacity: 0 !important;
    }

    &.v-list-item--active {
      background: rgba(var(--v-theme-primary), 0.12);
      color: rgb(var(--v-theme-primary));
    }

    &:hover:not(.v-list-item--active) {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }
  }
}

// Кнопки в header без фона
.app-header {
  .v-btn--variant-text,
  .v-btn--variant-plain,
  .v-btn--icon {
    background: transparent !important;

    &::before,
    &.v-btn--active::before,
    .v-btn__overlay {
      background: transparent !important;
      opacity: 0 !important;
    }
  }
}
</style>
